import styled, { css, keyframes } from 'styled-components'

import { Button } from 'components/Button'
import { SvgImage } from 'components/SvgImage'

import { Color } from 'root-constants'

const heartbeat = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(23, 23, 23, 0.16);
  }
  7% {
    transform: scale(1.035);
    box-shadow: 0 10px 24px rgba(23, 23, 23, 0.28);
  }
  14% {
    transform: scale(1.005);
    box-shadow: 0 5px 14px rgba(23, 23, 23, 0.18);
  }
  21% {
    transform: scale(1.022);
    box-shadow: 0 8px 18px rgba(23, 23, 23, 0.22);
  }
  32%,
  100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(23, 23, 23, 0.16);
  }
`

const sectionTitleText = css`
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: ${Color.INK_DARKEST};
`

const blockTitleText = css`
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  color: ${Color.INK_DARKEST};
`

const listItemTitleText = css`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${Color.INK_DARKEST};
`

const descriptionText = css`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${Color.INK_BASE};
`

const flexColumn = css`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`

const card = css`
  ${flexColumn};

  border-radius: 24px;
`

const listItem = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

export const StyledLandingWellness = {
  Wrapper: styled.div`
    ${flexColumn};

    min-height: 100%;
    background-color: ${Color.WHITE};
  `,
  DiscountBanner: styled.div`
    position: sticky;
    top: 0;
    z-index: 10;

    width: 100%;
    box-sizing: border-box;
    text-align: center;
    background: linear-gradient(90deg, #ca2f2f 0%, #7e0a1b 100%);

    padding: 4px 16px;
  `,
  DiscountBannerText: styled.p`
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: ${Color.WHITE};

    & strong {
      font-weight: inherit;
      color: ${Color.GOLD_LIGHTEST};
    }
  `,
  Content: styled.div`
    ${flexColumn};
    max-width: 375px;
    gap: 32px;

    padding: 16px 24px 24px;
    margin: 0 auto;
  `,
  Intro: styled.div`
    ${flexColumn};
    gap: 24px;
  `,
  Heading: styled.div`
    ${flexColumn};
    gap: 16px;
  `,
  HeroButton: styled.button`
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    border-radius: 24px;
    background: transparent;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${Color.INK_DARKEST};
      outline-offset: 4px;
    }
  `,
  DeskbookImage: styled.img`
    width: 100%;
    display: block;
    aspect-ratio: 515/446;
    object-fit: contain;
  `,
  Title: styled.p`
    ${sectionTitleText};
  `,
  Subtitle: styled.p`
    ${descriptionText};
  `,
  Benefits: styled.div`
    ${card};
    gap: 20px;
    background-color: ${Color.CORAL_GRAY};

    padding: 16px;
  `,
  Benefit: styled.div`
    ${listItem};
  `,
  ListItemIcon: styled(SvgImage)`
    flex-shrink: 0;
  `,
  ListItemContent: styled.div`
    ${flexColumn};
    min-width: 0;
    gap: 8px;
  `,
  ListItemTitle: styled.p`
    ${listItemTitleText};
  `,
  ListItemDescription: styled.p`
    ${descriptionText};
  `,
  Guarantee: styled.div`
    ${card};
    gap: 20px;
    background-color: ${Color.CORAL_GRAY};

    padding: 16px 24px 24px;
  `,
  GuaranteeBadge: styled.img`
    width: 124px;
    height: 124px;
    aspect-ratio: 1/1;
    object-fit: cover;
    align-self: center;
  `,
  GuaranteeBlock: styled.div`
    ${flexColumn};
    gap: 8px;
  `,
  BlockTitle: styled.p`
    ${blockTitleText};
  `,
  Outcomes: styled.div`
    ${flexColumn};
    gap: 24px;
  `,
  OutcomesTitle: styled.p`
    ${sectionTitleText};
    text-align: center;
  `,
  OutcomesList: styled.div`
    ${card};
    align-items: flex-start;
    gap: 24px;
    background: ${Color.GRAY_LIGHTEST};

    padding: 24px 16px;
  `,
  Outcome: styled.div`
    ${listItem};
    gap: 16px;
  `,
  OutcomeIconBadge: styled.div`
    flex-shrink: 0;

    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 50%;
    background: ${Color.GOLD_LIGHT};
    box-shadow: 0 6px 16px 0 rgba(161, 117, 2, 0.2);
  `,
  OutcomeDescription: styled.p`
    ${descriptionText};
    color: ${Color.INK_LIGHT};
  `,
  Features: styled.div`
    ${flexColumn};
    gap: 24px;
  `,
  Feature: styled.div`
    ${listItem};
  `,
  FeatureDescription: styled.p`
    ${descriptionText};
    white-space: pre-line;
  `,
  StickyCtaSection: styled.div`
    ${flexColumn};
    position: sticky;
    bottom: calc(24px + env(safe-area-inset-bottom));
    z-index: 2;

    &::before {
      content: '';
      position: absolute;
      top: -24px;
      bottom: calc(-24px - env(safe-area-inset-bottom));
      left: 50%;
      z-index: -1;

      width: 100vw;
      transform: translateX(-50%);
      pointer-events: none;
    }
  `,
  Button: styled(Button)<{ $isUppercase?: boolean }>`
    width: 100%;
    max-width: 100%;
    height: 48px;

    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
    white-space: nowrap;
    text-transform: ${({ $isUppercase }) =>
      $isUppercase ? 'uppercase' : 'none'};
    background: linear-gradient(
      92deg,
      ${Color.INK_DARKEST} 0%,
      ${Color.INK_DARK} 50%,
      ${Color.INK_DARKEST} 100%
    );
    color: ${Color.GOLD_LIGHTER};
    animation: ${heartbeat} 2.6s ease-in-out infinite;

    padding: 16px 32px;
  `,
}
